<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { format } from "date-fns";
import { fr } from "date-fns/locale";

import adminAPI from '@/services/api';
import { ReceiptRefundIcon } from '@heroicons/vue/20/solid';
import { EuroIcon, PlaneTakeoff } from 'lucide-vue-next';
import { InformationCircleIcon, DocumentMagnifyingGlassIcon, TrashIcon, PaperClipIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import type { AdditionalRequest, AdditionalRequests, ExpenseClaimDocument, File } from '@/interfaces/data-contracts';
import type { RetrieveClaimWithRelationsResponse, ClaimExpenses } from '@/interfaces';
import { useClaimStore, useNotificationStore } from '@/store';
import BaseButton from '../ui/buttons/BaseButton.vue';

import AlertModal from '@/components/ui/modals/AlertModal.vue';
import UpsertAdditionalRequest from './UpsertAdditionalRequest.vue';
import { ExpenseTypesMap } from '@/utils/config/constants';

const emits = defineEmits(["update"]); // Refetch claim

const props = defineProps<{
  additionalRequests?: AdditionalRequests;
}>();

const previewedFile = ref<{ data: string, file: File } | null>();
const modalRequest = ref(false);
const selectedRequest = ref<AdditionalRequest | null>(null);

async function deleteAdditionalRequest(claimId: string, requestType: string) {
  const { success } = await adminAPI.deleteAdditionalRequest(claimId, requestType);
  if (success) {
    emits("update");
    useNotificationStore().emitNotification({
      title: "Demande supprimée avec succès",
      type: "success",
    });
  }
}

function closeModal() {
  modalRequest.value = false;
  selectedRequest.value = null;
  useClaimStore().refreshClaim()
}

async function previewDocument(file?: File) {
  if (!file) return;
  const data = await adminAPI.retrieveFile(file.id)
  if (data) previewedFile.value = { data, file }
}
const tooltipFile = (document: ExpenseClaimDocument) => "Ajouté le " +
  format(new Date(document.createdAt), "d MMMM 'à' HH'h'mm", { locale: fr })

</script>
<template>

  <AlertModal
    :open="modalRequest"
    type="info"
    title="Ajouter une demande supplémentaire"
    confirm-message=""
    @close="closeModal"
    @cancel="closeModal"
    @confirm="closeModal"
    width="max-w-2xl"
  >
    <UpsertAdditionalRequest
      @upserted="closeModal"
      :claim-id="useClaimStore().claim.id"
      :selectedRequest="selectedRequest"
    />
  </AlertModal>

<div class="py-6 sm:px-6">

  <slot name="header" />

  <div class="pt-4">
    <ul v-if="additionalRequests?.length" role="list" class="w-full col-span-3 bg-white border border-gray-300 divide-y divide-gray-100 rounded-md shadow-sm">
        <li v-for="additionalRequest of additionalRequests" class="py-4 pl-4 pr-5 text-sm leading-6 ">
          <div class="flex items-center justify-between">
            <div class="flex items-center flex-1 w-0 cursor-pointer" @click="selectedRequest = additionalRequest; modalRequest = true">
              <span class="flex-shrink-0 w-5 h-5 text-gray-400" aria-hidden="true">
                <!-- <component :is="ExpenseTypesMap[expense.expenseType.type].icon" :size="20" /> -->
                <EuroIcon :size="20" />
              </span>
              <!-- <PlaneTakeoff :size="5" class="flex-shrink-0 w-5 h-5 text-gray-400" /> -->
              <div class="flex items-center flex-1 min-w-0 gap-2 ml-4">
                <span class="font-medium truncate">
                  <span class="">{{ additionalRequest.additionalRequestType.displayText }}</span>

                  <!-- @TODO Badge component -->
                  <span class="inline-flex ml-3 items-center gap-x-1.5 rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-700">
                    <svg class="h-1.5 w-1.5 fill-purple-500" viewBox="0 0 6 6" aria-hidden="true">
                      <circle cx="3" cy="3" r="3" />
                    </svg>
                    + {{ (additionalRequest.amountRequested / 100).toLocaleString("fr") }} €
                  </span>

                  <span v-if="additionalRequest.amountReceived" class="ml-3 text-xs text-green-600">(
                    + {{ (additionalRequest.amountReceived / 100).toLocaleString("fr") }}
                    <span> · Obtenu</span>
                  </span>
                </span>
              </div>
            </div>
            <div class="flex flex-shrink-0 ml-4 space-x-4" >
              <button type="button"
                v-if="additionalRequest.amountReceived"
                class="font-medium text-blue-600 rounded-md hover:text-blue-500"
              >
                <DocumentMagnifyingGlassIcon class="w-5 h-5" />
              </button>
              <span v-if="additionalRequest.amountReceived" class="text-gray-200" aria-hidden="true">|</span>
              <button type="button" class="font-medium text-gray-900 rounded-md hover:text-gray-800"
                @click="deleteAdditionalRequest(additionalRequest.claimId, additionalRequest.additionalRequestTypeName)"
              >
                <TrashIcon class="w-5 h-5" />
              </button>
            </div>
          </div>
          <div class="pt-2 text-xs italic pl-9">
            <!-- Quick solution for the first iteration -->
            <span class="text-slate-700" v-if="additionalRequest.reason">« <span class="font-semibold">{{ additionalRequest.reason }}</span> » · </span>
            Demande en cours
            <!-- Payé par {{ claimExpense.userClaimId ? claimExpense.paidBy : (claimExpense.paidBy === 'TRAVEL_AGENCY' ? 'l\'agence de voyages' : 'l\'entreprise') }}  <span v-if="!expense?.claimDocument"> · Justificatif manquant</span> -->
          </div>
        </li>
      </ul>
      <ul v-else  role="list" class="w-full col-span-3 mb-5 bg-white border border-gray-300 border-dotted divide-y divide-gray-100 rounded-md shadow-sm">
        <li class="flex items-center justify-between py-4 pl-4 pr-5 text-sm leading-6">
          <div class="flex items-center flex-1 w-0">
            <InformationCircleIcon class="flex-shrink-0 w-5 h-5 ml-2 text-gray-400" aria-hidden="true" />
            <div class="flex flex-1 min-w-0 gap-2 ml-4">
              <span class="flex items-center font-medium truncate">
                Aucune demande supplémentaire
              </span>
              <div class="ml-auto">
                <BaseButton text="Nouvelle demande" @click="modalRequest = !modalRequest"  />
                <!-- <BaseButton v-else text="Fermer" @click="modalRequest = false" :icon="XMarkIcon" /> -->
              </div>
            </div>
          </div>
        </li>
      </ul>
      <div v-if="additionalRequests?.length" class="mt-5 text-left">
        <BaseButton text="Nouvelle demande" @click="modalRequest = !modalRequest"  />
        <!-- <BaseButton v-else text="Fermer" @click="modalRequest = false" :icon="XMarkIcon" /> -->
      </div>
    </div>
  </div>
</template>